/**
 * Canonical textarea primitive — same API as <Input> but renders a
 * <textarea>. See Input.tsx for the prop docs.
 */

import React, { useId } from "react";
import { useTheme } from "../../context/ThemeContext";
import { getInputStyles } from "../../utils/themeUtils";
import { TOKENS, type InputSize } from "../../utils/designTokens";
import Label from "./Label";

interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  label?: React.ReactNode;
  size?: InputSize;
  error?: string;
  hint?: string;
  requiredMarker?: boolean;
  /**
   * Classes for the WRAPPER div, not the control. `className` reaches the
   * control only, so a grid span or a width constraint had no route in — two
   * separate sweeps wrapped the primitive in an extra div to get one.
   */
  wrapperClassName?: string;
  /**
   * Emit `w-full` on the wrapper. Defaults to true.
   *
   * Must be a prop rather than something `wrapperClassName` overrides, for the
   * same reason Card's `background` is: Tailwind resolves same-specificity
   * utilities by stylesheet SOURCE order, and `.w-full` is emitted AFTER the
   * numeric widths (measured in the built CSS: w-32 @33635, w-64 @33813,
   * w-full @34175). So a caller passing `w-32` would still render full width.
   */
  fullWidth?: boolean;
}

const Textarea: React.FC<TextareaProps> = ({
  label,
  size = "md",
  error,
  hint,
  requiredMarker,
  required,
  id: providedId,
  className = "",
  wrapperClassName = "",
  fullWidth = true,
  rows = 4,
  ...rest
}) => {
  const { isDarkMode } = useTheme();
  const generatedId = useId();
  const id = providedId || generatedId;

  const classes = [
    TOKENS.input.base,
    TOKENS.input.sizes[size],
    getInputStyles(isDarkMode),
    error ? "border-red-500 focus:ring-red-500" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const wrapperClasses = [fullWidth ? "w-full" : "", wrapperClassName]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={wrapperClasses}>
      {label && (
        <Label htmlFor={id} required={requiredMarker || required}>
          {label}
        </Label>
      )}
      <textarea
        id={id}
        rows={rows}
        required={required}
        className={classes}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        {...rest}
      />
      {error ? (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
  );
};

export default Textarea;
